import * as React from 'react'; // https://github.com/parcel-bundler/parcel/issues/1199
import * as propTypes from 'prop-types';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { logger, FormFieldComponentProps } from '@sheerid/jslib';

function isLeapYear(year)
{
    if (!year) {
        return;
    }
    return ((year % 4 == 0) && (year % 100 != 0)) || (year % 400 == 0);
}

const thisYear = new Date().getFullYear();
const numYears = 119;
const selectableYears = [] as number[];
for (var i = 0; i <= numYears; i++) {
    selectableYears.push(thisYear - i);
}

interface ModifiedBirthDateComponentProps {
    errorMessage?: string;
}

export const BirthDate = ({
    onChange,
    isErrored,
    value,
    errorMessage,
}: FormFieldComponentProps & ModifiedBirthDateComponentProps) => {
    const birthDateParts = value.split('-');
    const birthDateYear = Number.parseInt(birthDateParts[0], 10) || undefined;
    const birthDateMonth = Number.parseInt(birthDateParts[1], 10) || undefined;
    const birthDateDay = Number.parseInt(birthDateParts[2], 10) || undefined;

    const updateBirthMonth = (month) => {
        if (month === '' || month === null) {
            onChange(`${birthDateYear}--${birthDateDay}`);
        }

        const newVal = Number.parseInt(month, 10);
        if (typeof newVal === 'number' && newVal >= 1 && newVal <= 12) {
            onChange(`${birthDateYear}-${month}-${birthDateDay}`);
        } else {
            logger.error(`Value '${newVal}' is an invalid birth month`);
        }
    };

    const updateBirthDay = (day) => {
        if (day === '') {
            onChange(`${birthDateYear}-${birthDateMonth}-`);
        }
        const newVal = Number.parseInt(day, 10);
        if (typeof newVal === 'number' && newVal >= 1 && newVal <= 31) {
            onChange(`${birthDateYear}-${birthDateMonth}-${newVal}`);
        } else {
            logger.error(`Value '${newVal}' is an invalid birth day`);
        }
    };

    const updateBirthYear = (year) => {
        if (year === '') {
            onChange(`-${birthDateMonth}-${birthDateDay}`);
        }
        const newVal = Number.parseInt(year, 10);
        if (typeof newVal === 'number' && newVal >= 1 && newVal <= thisYear) {
            onChange(`${newVal}-${birthDateMonth}-${birthDateDay}`);
        } else {
            logger.error(`Value '${newVal}' is an invalid birth year`);
        }
    };

    const getBirthDate = () => {
        onChange(`${birthDateYear}-${birthDateMonth}-${birthDateDay}`);
    };

    return (
        <div className="sid-field sid-birthdate">
            <div className="sid-l-space-top-md" />
            <label>
                <div className="sid-field__label">
                    Date of Birth
                </div>
            </label>

            <div className="sid-birthdate2__inputs">
                <select
                    className={`sid-birthdate2__month sid-text-input sid-text-input--required ${isErrored ? 'sid-text-input--error' : ''}`}
                    onChange={e => updateBirthMonth(e.target.value)}
                    onBlur={getBirthDate}
                    value={birthDateMonth}
                >
                    { !birthDateMonth ? <option>Month</option> : null }
                    <option value="1" key="1">Jan</option>
                    <option value="2" key="2">Feb</option>
                    <option value="3" key="3">Mar</option>
                    <option value="4" key="4">Apr</option>
                    <option value="5" key="5">May</option>
                    <option value="6" key="6">Jun</option>
                    <option value="7" key="7">Jul</option>
                    <option value="8" key="8">Aug</option>
                    <option value="9" key="9">Sep</option>
                    <option value="10" key="10">Oct</option>
                    <option value="11" key="11">Nov</option>
                    <option value="12" key="12">Dec</option>
                </select>

                <select
                    className={`sid-birthdate2__day sid-text-input sid-text-input--required ${isErrored ? 'sid-text-input--error' : ''}`}
                    onChange={e => updateBirthDay(e.target.value)}
                    onBlur={getBirthDate}
                    value={birthDateDay}
                >
                    {/* Hide "Day" option once a day has been selected */}
                    { !birthDateDay ? <option>Day</option> : null }

                    {/* All months have 28 days, show always  */}
                    {
                        [...Array(28)].map((e, i) => {
                            i += 1;
                            return <option value={i} key={i}>{i}</option>
                        })
                    }

                    {/* All months except February have a 29th day, unless it's a leap year. */}
                    {
                        birthDateMonth && (birthDateMonth !== 2 || isLeapYear(birthDateYear)) ? <option value="29" key="29">29</option>: null
                    }

                    {/* Some months have 30 days */}
                    {
                        (birthDateMonth !== 2) && birthDateMonth ? <option value="30" key="30">30</option> : null
                    }

                    {/* Some months have 31 days */}
                    {
                        (birthDateMonth === 1
                        || birthDateMonth === 3
                        || birthDateMonth === 5
                        || birthDateMonth === 7
                        || birthDateMonth === 8
                        || birthDateMonth === 10
                        || birthDateMonth === 12) && birthDateMonth ? <option value="31" key="31">31</option> : null
                    }

                </select>


                <select
                    className={`sid-birthdate2__year sid-text-input sid-text-input--required ${isErrored ? 'sid-text-input--error' : ''}`}
                    onChange={e => updateBirthYear(e.target.value)}
                    onBlur={getBirthDate}
                    value={birthDateYear}
                >
                    {/* Hide "Year" option once a Year has been selected */}
                    { !birthDateYear ? <option>Year</option> : null }

                    {
                        selectableYears.map((e, i) => {
                            i += 1;
                            return <option value={e} key={e}>{e}</option>
                        })
                    }
                </select>

            </div>

            {
                isErrored ? (
                    <div className="sid-field-error">
                        <FormattedHTMLMessage
                            id={`errorId.${errorMessage ? errorMessage : "invalidBirthDate"}`}
                            defaultMessage="Invalid birth date"
                        />
                    </div>
                ) : null
            }
        </div>
    );
};

BirthDate.propTypes = {
    value: propTypes.string,
    onChange: propTypes.func.isRequired,
    isErrored: propTypes.bool.isRequired,
};

BirthDate.defaultProps = {
    value: '2000-01-01',
};

export const BirthDateComponent = injectIntl(BirthDate);
